import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  EventEmitter,
} from "@angular/core";
import { Chart, ChartConfiguration, ChartOptions, Plugin } from "chart.js/auto";
import { FormControl, FormGroup } from "@angular/forms";
import { StationDto, AlertLevels } from "src/app/services/api";
import {
  LABELS_COLOR,
  GRID_LINES_COLOR,
  FONT,
  CHART_LEVEL1_ZONE_COLOR,
  CHART_LEVEL2_ZONE_COLOR,
  CHART_LEVEL3_ZONE_COLOR,
  CHART_LEVEL4_ZONE_COLOR,
} from "src/app/constants/index";
import { WindSpeedAlertLevels } from "src/app/interfaces";
import "chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm";
import { TranslateService } from "@ngx-translate/core";

@Directive()
export abstract class BaseChartComponent implements OnChanges, AfterViewInit {
  @ViewChild("chartContainer") protected chartContainer!: ElementRef<HTMLCanvasElement>;

  @Input() public data!: Array<{ timestamp: string | number; value: number }>;
  @Input() public currentTime!: Date;
  @Input() public stations?: StationDto[];
  // Input opcional para la lógica del formulario
  @Input() public selectedStationCode?: string;

  // Se agregan estos inputs opcionales para los headers de precipitación
  @Input() public precipitationLast1h?: string;
  @Input() public precipitationLast24h?: string;
  @Input() public windSpeedAlertLevels?: WindSpeedAlertLevels;
  @Input() public alertLevels?: AlertLevels;
  @Input() public showCurrentTimeLine?: boolean;
  @Output() public readonly selectStation: EventEmitter<string> = new EventEmitter();
  public form!: FormGroup;
  protected chart?: Chart;

  constructor(
    private translate: TranslateService,
    protected cdr: ChangeDetectorRef,
  ) {}

  protected legendSpacingPlugin: Plugin = {
    id: "legendSpacing",
    beforeInit(chart) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const originalFit = (chart.legend as any).fit;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (chart.legend as any).fit = function fit(): void {
        originalFit.bind(chart.legend)();
        this.height += 10; // Add 10px of space below the legend
      };
    },
  };

  protected drawCurrentTimeLinePlugin: Plugin = {
    id: "currentTimeLine",
    afterDraw: chart => {
      if (!this.showCurrentTimeLine) return;
      const {
        ctx,
        chartArea: { top, bottom, left, right },
        scales: { x },
      } = chart;
      const now = new Date().getTime();
      const xCoord = x.getPixelForValue(now);

      if (xCoord >= left && xCoord <= right) {
        ctx.save();
        // Dashed line
        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(xCoord, top);
        ctx.lineTo(xCoord, bottom);
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#4b5563"; // gray-600
        ctx.stroke();

        // Label
        const label = this.translate.instant("STATION_PAGE.CURRENT_TIME");
        ctx.fillStyle = "#4b5563"; // gray-600
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(label, xCoord, top - 5);

        ctx.restore();
      }
    },
  };

  public ngAfterViewInit(): void {
    if (this.chartContainer && this.data) {
      this.renderChart();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.chartContainer && changes.data && this.data) {
      setTimeout(() => {
        this.renderChart();
        this.cdr.detectChanges();
      }, 0);
    }

    this.updateStationForm(changes);
  }

  protected renderChart(): void {
    if (!this.data) {
      return;
    }
    const config: ChartConfiguration = this.getChartConfig();
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
    this.chart = new Chart(this.chartContainer.nativeElement, config);
  }

  /**
   * Método que encapsula la lógica del formulario para la selección de estación.
   */
  protected updateStationForm(changes: SimpleChanges): void {
    if (!this.form) {
      this.form = new FormGroup({ station: new FormControl(null) });
    }

    if (this.selectedStationCode && changes.selectedStationCode) {
      this.form.patchValue({ station: this.selectedStationCode });
    }
  }

  protected getCommonChartOptions(
    xSuggestedMax: number | undefined,
    ySuggestedMax: number,
    yLabelUnit: string = "",
    ySuggestedMin: number | null = null,
  ): ChartOptions {
    return {
      plugins: {
        legend: { display: false },
      },
      interaction: {
        intersect: false,
        mode: "nearest",
      },
      aspectRatio: 1.5,
      scales: {
        x: {
          type: "time",
          time: {
            unit: "minute",
            tooltipFormat: "YYYY-MM-DD HH:mm",
            displayFormats: { minute: "HH:mm", day: "YYYY-MM-DD HH:mm" },
          },
          grid: { color: GRID_LINES_COLOR, lineWidth: 1 },
          ticks: {
            font: FONT,
            color: LABELS_COLOR,
            stepSize: 15,
          },
          suggestedMax: xSuggestedMax,
        },
        y: {
          min: 0,
          suggestedMax: ySuggestedMax,
          suggestedMin: ySuggestedMin ?? 0,
          grid: { color: GRID_LINES_COLOR, lineWidth: 1 },
          ticks: {
            font: FONT,
            color: LABELS_COLOR,
            callback: (value: number | string): string => `${value}${yLabelUnit}`,
          },
        },
      },
    };
  }

  /**
   * Plugin unificado para dibujar niveles de alerta en gráficos
   * @param type Tipo de gráfico ('discharge' o 'water')
   * @returns Plugin de Chart.js configurado
   */
  protected drawAlertLevelsPlugin(type: "discharge" | "water"): Plugin {
    return {
      id: `alert-levels-${type}`,
      beforeDraw: (): void => {
        if (this.chart && this.alertLevels) {
          const {
            ctx,
            chartArea: { left, top, right, bottom },
            scales: { y },
          } = this.chart;

          let level1, level2, level3;
          if (type === "water") {
            // Water level logic (centimeters to meters conversion)
            level1 = this.alertLevels?.level1Centimeters
              ? y.getPixelForValue(this.alertLevels.level1Centimeters / 100)
              : this.alertLevels?.level1Discharge
                ? y.getPixelForValue(this.alertLevels.level1Discharge)
                : null;
            level2 = this.alertLevels?.level2Centimeters
              ? y.getPixelForValue(this.alertLevels.level2Centimeters / 100)
              : this.alertLevels?.level2Discharge
                ? y.getPixelForValue(this.alertLevels.level2Discharge)
                : null;
            level3 = this.alertLevels?.level3Centimeters
              ? y.getPixelForValue(this.alertLevels.level3Centimeters / 100)
              : this.alertLevels?.level3Discharge
                ? y.getPixelForValue(this.alertLevels.level3Discharge)
                : null;
          } else {
            // Discharge logic (direct values)
            level1 = this.alertLevels?.level1Discharge ? y.getPixelForValue(this.alertLevels.level1Discharge) : null;
            level2 = this.alertLevels?.level2Discharge ? y.getPixelForValue(this.alertLevels.level2Discharge) : null;
            level3 = this.alertLevels?.level3Discharge ? y.getPixelForValue(this.alertLevels.level3Discharge) : null;
          }

          // Common fallback logic
          level3 = level3 || top;
          level2 = level2 || level3;
          level1 = level1 || level2;

          // Common drawing logic
          ctx.save();

          ctx.fillStyle = CHART_LEVEL1_ZONE_COLOR;
          ctx.fillRect(left, bottom, right, level1 - bottom);

          ctx.fillStyle = CHART_LEVEL2_ZONE_COLOR;
          ctx.fillRect(left, level1, right, level2 - level1);

          ctx.fillStyle = CHART_LEVEL3_ZONE_COLOR;
          ctx.fillRect(left, level2, right, level3 - level2);

          ctx.fillStyle = CHART_LEVEL4_ZONE_COLOR;
          ctx.fillRect(left, level3, right, top - level3);

          ctx.restore();
        }
      },
    };
  }

  protected abstract getChartConfig(): ChartConfiguration;

  // Método para trackBy en la iteración de estaciones
  public trackByStationId(index: number, station: StationDto): string {
    return station.id;
  }
}
